/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { applyThemeVars } from "../../context/ThemeContext";
import { api } from "../../lib/api";
import { Modal, Field, SignalBars, AvatarUpload } from "../common";

const BUILTIN_PRESETS = [
  { name: "RÉPLA // STANDARD", theme: { color_primary: "#2DD4BF", color_secondary: "#EAB308", color_accent: "#2DD4BF", color_alert: "#EF4444", color_void: "#07090D", color_panel: "#0D1117", color_surface: "#131922", color_text: "#F1F5F9", font_title: "Chivo", font_body: "IBM Plex Sans", network_name: "RÉPLA // COM" } },
  { name: "CORPORATE TERMINAL", theme: { color_primary: "#38BDF8", color_secondary: "#64748B", color_accent: "#38BDF8", color_alert: "#F43F5E", color_void: "#0A0E14", color_panel: "#111827", color_surface: "#1F2937", color_text: "#E5E7EB", font_title: "Oxanium", font_body: "IBM Plex Sans", network_name: "CORPORATE // NET" } },
  { name: "TERMINAL MILITAIRE", theme: { color_primary: "#84CC16", color_secondary: "#A16207", color_accent: "#84CC16", color_alert: "#DC2626", color_void: "#0B0F0A", color_panel: "#13180F", color_surface: "#1A2113", color_text: "#E7F0D8", font_title: "Rajdhani", font_body: "JetBrains Mono", network_name: "MIL-NET // SECURE" } },
  { name: "INTERFACE CIVILE", theme: { color_primary: "#F59E0B", color_secondary: "#0EA5E9", color_accent: "#F59E0B", color_alert: "#EF4444", color_void: "#0E0B07", color_panel: "#171210", color_surface: "#211A14", color_text: "#F5EFE6", font_title: "Chivo", font_body: "IBM Plex Sans", network_name: "CIVIC // LINK" } },
];

const FONTS = ["Chivo", "IBM Plex Sans", "JetBrains Mono", "Orbitron", "Rajdhani", "Oxanium", "Space Mono"];
const COLORS = [
  ["color_primary", "PRIMAIRE"], ["color_secondary", "SECONDAIRE"], ["color_accent", "ACCENT"],
  ["color_alert", "ALERTE"], ["color_void", "FOND"], ["color_panel", "PANNEAU"],
  ["color_surface", "SURFACE"], ["color_text", "TEXTE"],
];
const TERMS = [["messages", "MESSAGES"], ["contact", "CONTACT"], ["online", "EN LIGNE"], ["feeds", "FLUX"]];

export default function ThemeEditor({ open, onClose, title = "ÉDITEUR DE THÈME", baseTheme = {}, baseNetwork = "RÉPLA // COM", baseTerminology = {}, allowInherit = false, initialMode = "custom", onSave }) {
  const [t, setT] = useState(baseTheme);
  const [mode, setMode] = useState(initialMode);
  const [network, setNetwork] = useState(baseNetwork);
  const [terms, setTerms] = useState(baseTerminology);
  const [presets, setPresets] = useState([]);

  useEffect(() => {
    if (open) {
      setT(baseTheme || {});
      setMode(initialMode);
      setNetwork(baseNetwork || "RÉPLA // COM");
      setTerms(baseTerminology || {});
      api.get("/presets").then((r) => setPresets(r.data)).catch(() => setPresets([]));
    }
    // eslint-disable-next-line
  }, [open]);

  function applyPreset(p) {
    const nt = { ...t, ...p.theme };
    setT(nt);
    if (p.theme.network_name) setNetwork(p.theme.network_name);
    if (p.terminology) setTerms({ ...terms, ...p.terminology });
    applyThemeVars(nt);
  }
  async function savePreset() {
    const name = window.prompt("Nom du preset :");
    if (!name) return;
    await api.post("/presets", { name: name.trim(), theme: { ...t, network_name: network }, terminology: terms });
    const r = await api.get("/presets");
    setPresets(r.data);
  }
  async function removePreset(id) {
    await api.delete(`/presets/${id}`);
    setPresets((ps) => ps.filter((p) => p.id !== id));
  }

  function update(k, v) {
    const n = { ...t, [k]: v };
    setT(n);
    applyThemeVars(n);
  }
  const editable = !allowInherit || mode === "custom";

  async function save() {
    await onSave?.({ theme: { ...t, network_name: network }, network, mode, terminology: terms });
    onClose();
  }
  function cancel() {
    applyThemeVars(baseTheme);
    onClose();
  }

  return (
    <Modal open={open} onClose={cancel} title={title} width={760}>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          {allowInherit && (
            <div className="flex gap-2 mb-4">
              {["inherit", "custom"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`theme-mode-${m}`} className="btn text-[10px] flex-1" style={{ background: mode === m ? "var(--r-primary)" : "transparent", color: mode === m ? "#04131a" : "var(--r-text)" }}>
                  {m === "inherit" ? "THÈME DE L'UNIVERS" : "PERSONNALISER"}
                </button>
              ))}
            </div>
          )}

          <div style={{ opacity: editable ? 1 : 0.4, pointerEvents: editable ? "auto" : "none" }}>
            <div className="sys-label mb-2">PRESETS</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {BUILTIN_PRESETS.map((p) => (
                <button key={p.name} data-testid={`preset-builtin-${p.name}`} onClick={() => applyPreset(p)} className="btn btn-ghost text-[10px]">{p.name}</button>
              ))}
            </div>
            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {presets.map((p) => (
                  <span key={p.id} className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono" style={{ border: "1px solid color-mix(in srgb, var(--r-secondary) 40%, transparent)", color: "var(--r-secondary)" }}>
                    <button data-testid={`preset-apply-${p.id}`} onClick={() => applyPreset(p)}>{p.name}</button>
                    <button data-testid={`preset-del-${p.id}`} onClick={() => removePreset(p.id)} className="opacity-70 hover:opacity-100">✕</button>
                  </span>
                ))}
              </div>
            )}
            <button data-testid="preset-save-button" onClick={savePreset} className="btn btn-ghost text-[10px] w-full mb-4">＋ ENREGISTRER COMME PRESET</button>

            <Field label="NOM DU RÉSEAU">
              <input data-testid="theme-network-input" className="input font-mono" value={network} onChange={(e) => setNetwork(e.target.value)} />
            </Field>

            <div className="grid grid-cols-2 gap-3 mb-1">
              <div>
                <span className="sys-label block mb-1.5">LOGO</span>
                <AvatarUpload value={t.logo} name="LOGO" size={48} onChange={(p) => update("logo", p)} />
              </div>
              <div>
                <span className="sys-label block mb-1.5">IMAGE DE FOND</span>
                <AvatarUpload value={t.background_image} name="FOND" size={48} onChange={(p) => update("background_image", p)} />
              </div>
            </div>

            <div className="sys-label mb-2">COULEURS</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {COLORS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 frame px-2 py-1.5">
                  <input data-testid={`theme-color-${k}`} type="color" value={t[k] || "#000000"} onChange={(e) => update(k, e.target.value)} style={{ width: 26, height: 26, border: "none", background: "none" }} />
                  <span className="sys-label">{label}</span>
                </label>
              ))}
            </div>

            <Field label={`SCANLINES · ${Math.round((t.scanline_intensity ?? 0.025) * 1000) / 10}%`}>
              <input type="range" min="0" max="0.2" step="0.005" value={t.scanline_intensity ?? 0.025} onChange={(e) => update("scanline_intensity", parseFloat(e.target.value))} className="w-full" />
            </Field>
            <Field label={`GLITCH · ${Math.round((t.glitch_intensity ?? 0.15) * 100)}%`}>
              <input type="range" min="0" max="1" step="0.05" value={t.glitch_intensity ?? 0.15} onChange={(e) => update("glitch_intensity", parseFloat(e.target.value))} className="w-full" />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <Field label="POLICE TITRES">
                <select className="input font-mono text-xs" value={t.font_title || "Chivo"} onChange={(e) => update("font_title", e.target.value)}>
                  {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="POLICE TEXTE">
                <select className="input font-mono text-xs" value={t.font_body || "IBM Plex Sans"} onChange={(e) => update("font_body", e.target.value)}>
                  {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
            </div>

            <div className="sys-label mb-2 mt-1">TERMINOLOGIE</div>
            <div className="grid grid-cols-2 gap-2">
              {TERMS.map(([k, label]) => (
                <Field key={k} label={label}>
                  <input data-testid={`theme-term-${k}`} className="input font-mono text-xs" value={terms[k] || ""} onChange={(e) => setTerms({ ...terms, [k]: e.target.value })} />
                </Field>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="sys-label mb-2">APERÇU</div>
          <div className="frame p-3" style={{ background: "var(--r-void)" }}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-title font-black text-glow">{network}</span>
              <SignalBars />
            </div>
            <div className="space-y-2">
              <div className="p-2" style={{ background: "var(--r-surface)", border: "1px solid color-mix(in srgb, var(--r-text-muted) 22%, transparent)" }}>
                <div className="text-xs font-semibold" style={{ color: "var(--r-text)" }}>Vincenzo</div>
                <div className="text-xs" style={{ color: "var(--r-text-muted)" }}>Monte au premier étage. Maintenant.</div>
              </div>
              <div className="p-2 ml-6" style={{ background: "color-mix(in srgb, var(--r-primary) 16%, var(--r-surface))", border: "1px solid color-mix(in srgb, var(--r-primary) 45%, transparent)" }}>
                <div className="text-xs" style={{ color: "var(--r-text)" }}>J'arrive.</div>
              </div>
              <div className="flex gap-2 mt-2">
                <span className="btn btn-solid text-[9px]">ENVOYER</span>
                <span className="px-2 py-1 text-[9px] font-mono" style={{ border: "1px solid var(--r-alert)", color: "var(--r-alert)" }}>FLASH</span>
              </div>
            </div>
          </div>
          <div className="sys-label mt-3">Les modifications s'appliquent en direct.</div>
        </div>
      </div>

      <div className="flex gap-2 mt-5">
        <button className="btn btn-ghost flex-1" onClick={cancel}>ANNULER</button>
        <button data-testid="theme-save-button" className="btn btn-solid flex-1" onClick={save}>APPLIQUER</button>
      </div>
    </Modal>
  );
}
